import React, { useState } from "react";
import DatePicker from "react-datepicker";
import { X } from "lucide-react";

import { format } from "date-fns/format";
import { startOfWeek } from "date-fns/startOfWeek";
import { endOfWeek } from "date-fns/endOfWeek";
import { startOfMonth } from "date-fns/startOfMonth";
import { endOfMonth } from "date-fns/endOfMonth";

export type FilterMode = "day" | "week" | "month" | "custom";

interface DateFilterPopoverProps {
  onClose: () => void;

  onApply: (filterData: {
    mode: FilterMode;
    displayLabel: string;
    value: any;
  }) => void;
}

const DateFilterPopover: React.FC<DateFilterPopoverProps> = ({
  onClose,
  onApply,
}) => {
  // ------------------------------------------------
  // Default filter
  // ------------------------------------------------

  const [mode, setMode] = useState<FilterMode>("week");

  // ------------------------------------------------
  // Date state
  // ------------------------------------------------

  const today = new Date();

  const [singleDate, setSingleDate] = useState<Date | null>(today);

  const [startDate, setStartDate] = useState<Date | null>(
    startOfWeek(today, {
      weekStartsOn: 1,
    }),
  );

  const [endDate, setEndDate] = useState<Date | null>(
    endOfWeek(today, {
      weekStartsOn: 1,
    }),
  );

  const [monthDate, setMonthDate] = useState<Date | null>(today);

  // ------------------------------------------------
  // Tab change
  // ------------------------------------------------

  const handleModeChange = (newMode: FilterMode) => {
    setMode(newMode);
  };

  // ------------------------------------------------
  // Today
  // ------------------------------------------------

  const handleSingleChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    setSingleDate(date);
  };

  // ------------------------------------------------
  // Week
  // ------------------------------------------------

  const handleWeekChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);
  };

  // ------------------------------------------------
  // Month
  // ------------------------------------------------

  const handleMonthChange = (date: Date | null) => {
    if (!date) {
      return;
    }

    setMonthDate(date);
  };

  // ------------------------------------------------
  // Apply
  // ------------------------------------------------

  const handleApply = () => {
    // ----------------------------------------------
    // TODAY
    // ----------------------------------------------

    if (mode === "day") {
      if (!singleDate) {
        return;
      }

      onApply({
        mode: "day",

        displayLabel: format(singleDate, "MMM dd, yyyy"),

        value: singleDate,
      });

      return;
    }

    // ----------------------------------------------
    // WEEK
    // ----------------------------------------------

    if (mode === "week") {
      if (!startDate || !endDate) {
        return;
      }

      onApply({
        mode: "week",

        displayLabel: `${format(startDate, "MMM dd")} - ${format(
          endDate,
          "MMM dd, yyyy",
        )}`,

        value: {
          start: startDate,
          end: endDate,
        },
      });

      return;
    }

    // ----------------------------------------------
    // MONTH
    // ----------------------------------------------

    if (mode === "month") {
      if (!monthDate) {
        return;
      }

      const start = startOfMonth(monthDate);

      const end = endOfMonth(monthDate);

      onApply({
        mode: "month",

        displayLabel: format(monthDate, "MMMM yyyy"),

        value: {
          start,
          end,
        },
      });

      return;
    }

    // ----------------------------------------------
    // CUSTOM
    // ----------------------------------------------

    if (mode === "custom") {
      if (!startDate || !endDate) {
        return;
      }

      onApply({
        mode: "custom",

        displayLabel: `${format(startDate, "MMM dd, yyyy")} - ${format(
          endDate,
          "MMM dd, yyyy",
        )}`,

        value: {
          start: startDate,
          end: endDate,
        },
      });
    }
  };

  // ------------------------------------------------
  // Display helpers
  // ------------------------------------------------

  const getTodayLabel = () => {
    if (!singleDate) {
      return "Select date";
    }

    return format(singleDate, "MMM dd, yyyy");
  };

  const getWeekLabel = () => {
    if (!startDate || !endDate) {
      return "Select week";
    }

    return `${format(startDate, "MMM dd")} - ${format(
      endDate,
      "MMM dd, yyyy",
    )}`;
  };

  const getMonthLabel = () => {
    if (!monthDate) {
      return "Select month";
    }

    return format(monthDate, "MMMM yyyy");
  };

  // ------------------------------------------------
  // Render
  // ------------------------------------------------

  return (
    <div
      className="
        fixed
        sm:absolute
        left-1/2
        -translate-x-1/2
        sm:left-auto
        sm:translate-x-0
        sm:right-0
        top-[75px]
        sm:top-auto
        mt-2

        bg-white
        rounded-2xl
        shadow-xl
        border
        border-gray-100

        p-4

        z-50

        w-[310px]
        max-w-[calc(100vw-24px)]
      "
    >
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div
        className="
          flex
          items-center
          justify-between
          pb-3
          mb-3
          border-b
          border-gray-100
        "
      >
        <span
          className="
            font-bold
            text-sm
            text-logoHeading
          "
        >
          Filter Range
        </span>

        <button
          type="button"
          onClick={onClose}
          className="
            text-gray-400
            hover:text-gray-600
            transition-colors
          "
        >
          <X size={18} />
        </button>
      </div>

      {/* ================================================= */}
      {/* TABS */}
      {/* ================================================= */}

      <div
        className="
          grid
          grid-cols-4
          gap-1
          mb-4
          bg-gray-50
          p-1
          rounded-xl
          text-xs
          font-semibold
        "
      >
        {(["day", "week", "month", "custom"] as FilterMode[]).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => handleModeChange(type)}
            className={`
              py-2
              px-1
              capitalize
              rounded-lg
              transition-all
              whitespace-nowrap

              ${
                mode === type
                  ? `
                    bg-white
                    text-logoHeading
                    shadow-sm
                  `
                  : `
                    text-gray-500
                    hover:text-gray-700
                  `
              }
            `}
          >
            {type === "day"
              ? "Today"
              : type === "week"
                ? "Week"
                : type === "month"
                  ? "Month"
                  : "Custom"}
          </button>
        ))}
      </div>

      {/* ================================================= */}
      {/* TODAY */}
      {/* ================================================= */}

      {mode === "day" && (
        <div>
          <div
            className="
              flex
              justify-center
              min-h-[280px]
            "
          >
            <DatePicker
              selected={singleDate}
              onChange={handleSingleChange}
              inline
            />
          </div>

          <div
            className="
              mt-3
              text-center
              text-xs
              text-gray-500
            "
          >
            Dashboard Date
          </div>

          <div
            className="
              mt-1
              text-center
              text-lg
              font-medium
              text-[#2F5E2E]
            "
          >
            {getTodayLabel()}
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* WEEK */}
      {/* ================================================= */}

      {mode === "week" && (
        <div>
          <div
            className="
              flex
              justify-center
              min-h-[280px]
            "
          >
            <DatePicker
              selected={startDate}
              onChange={handleWeekChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
            />
          </div>

          <div
            className="
              mt-3
              text-center
              text-xs
              text-gray-500
            "
          >
            Selected Week
          </div>

          <div
            className="
              mt-1
              text-center
              text-sm
              font-medium
              text-[#2F5E2E]
            "
          >
            {getWeekLabel()}
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* MONTH */}
      {/* ================================================= */}

      {mode === "month" && (
        <div>
          <div
            className="
              flex
              justify-center
              min-h-[280px]
            "
          >
            <DatePicker
              selected={monthDate}
              onChange={handleMonthChange}
              showMonthYearPicker
              dateFormat="MMMM yyyy"
              inline
            />
          </div>

          <div
            className="
              mt-3
              text-center
              text-xs
              text-gray-500
            "
          >
            Selected Month
          </div>

          <div
            className="
              mt-1
              text-center
              text-sm
              font-medium
              text-[#2F5E2E]
            "
          >
            {getMonthLabel()}
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* CUSTOM */}
      {/* ================================================= */}

      {mode === "custom" && (
        <div>
          <div
            className="
              flex
              justify-center
              min-h-[280px]
            "
          >
            <DatePicker
              selected={startDate}
              onChange={handleWeekChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
            />
          </div>

          <div
            className="
              mt-3
              text-center
              text-xs
              text-gray-500
            "
          >
            Custom Date Range
          </div>

          <div
            className="
              mt-1
              text-center
              text-sm
              font-medium
              text-[#2F5E2E]
            "
          >
            {getWeekLabel()}
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* APPLY BUTTON */}
      {/* ================================================= */}

      <button
        type="button"
        onClick={handleApply}
        className="
          w-full
          mt-4
          py-3
          rounded-lg
          bg-[#2F5E2E]
          text-white
          text-sm
          font-semibold
          hover:bg-[#244B24]
          transition-colors
        "
      >
        {mode === "day"
          ? "Apply Today"
          : mode === "week"
            ? "Apply Week"
            : mode === "month"
              ? "Apply Month"
              : "Apply Custom Range"}
      </button>
    </div>
  );
};

export default DateFilterPopover;
